import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../store';
import type { KPIFilterDropdownItem } from '../../../AnalyticsHub/components/KPIFilterDropdown';
import { useDisabledPeriodWeekIds } from '../../../AnalyticsHub/hooks/useDisabledPeriodWeekIds';
import {
  locationOptions,
  marketOptions,
  normalizeFilterValue,
  periodOptions,
  regionOptions,
  useWesFilterReference,
  weekOptions,
  yearOptions,
  type WesCurrentPeriodWeek,
  type WesReference,
} from './wesFilterReference';
import {
  WES_PERIODS_PER_YEAR,
  WES_QUARTER_START_PERIODS,
  WES_WEEKS_PER_PERIOD,
  type WesQuickSelectKey,
} from './wesPalletStagingStaticData';

// Selection ids use the same formats as the Crewing dropdowns: region/market names, locId, '2026', 'P5', 'P5W2'.
export interface WesFilterSelection {
  regions: string[];
  markets: string[];
  locations: string[];
  years: string[];
  periods: string[];
  weeks: string[];
}

export type WesFilterKey = keyof WesFilterSelection;
type Timeframe = Pick<WesFilterSelection, 'years' | 'periods' | 'weeks'>;

export const EMPTY_WES_SELECTION: WesFilterSelection = {
  regions: [],
  markets: [],
  locations: [],
  years: [],
  periods: [],
  weeks: [],
};

const selectionForFiscalYear = (year?: number | null): WesFilterSelection => ({
  ...EMPTY_WES_SELECTION,
  years: year ? [String(year)] : [],
});

const sameList = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

const sameTimeframe = (a: Timeframe, b: Timeframe) =>
  sameList(a.years, b.years) && sameList(a.periods, b.periods) && sameList(a.weeks, b.weeks);

export const sameWesSelection = (a: WesFilterSelection, b: WesFilterSelection) =>
  sameList(a.regions, b.regions) &&
  sameList(a.markets, b.markets) &&
  sameList(a.locations, b.locations) &&
  sameTimeframe(a, b);

// Parent changes clear child selections that are no longer valid.
const prune = (sel: WesFilterSelection, ref: WesReference): WesFilterSelection => {
  const markets = sel.markets.filter((id) => marketOptions(ref.locations, sel.regions).some((o) => String(o.id) === id));
  const locations = sel.locations.filter((id) =>
    locationOptions(ref.locations, sel.regions, markets).some((o) => String(o.id) === id),
  );
  const weeks = sel.weeks.filter((id) => weekOptions(ref.periodWeeks, sel.periods).some((o) => String(o.id) === id));
  return { ...sel, markets, locations, weeks };
};

// Selecting locations fills Region and Market from them (and clears both when none remain), as Crewing does.
const distinctFromLocations = (ref: WesReference, locationIds: string[], field: 'region' | 'market'): string[] => {
  const ids = new Set(locationIds.map(String));
  const seen = new Map<string, string>();
  ref.locations
    .filter((loc) => ids.has(String(loc.locId)))
    .forEach((loc) => {
      const value = loc[field]?.trim();
      if (value) seen.set(normalizeFilterValue(value), value);
    });
  return Array.from(seen.values());
};

// ─── Quick selects (same math as Crewing / Perfect Truck over the current period and week) ──────────

type PeriodWeek = { period: number; weekInPeriod: number };

const timeframeFrom = (year: number, entries: PeriodWeek[]): Timeframe => ({
  years: [String(year)],
  periods: Array.from(new Set(entries.map((e) => e.period))).map((p) => `P${p}`),
  weeks: entries.map((e) => `P${e.period}W${e.weekInPeriod}`),
});

const weeksOfPeriod = (period: number, upTo = WES_WEEKS_PER_PERIOD): PeriodWeek[] =>
  Array.from({ length: upTo }, (_, i) => ({ period, weekInPeriod: i + 1 }));

export const quickSelectTimeframe = (key: WesQuickSelectKey, current: WesCurrentPeriodWeek): Timeframe => {
  const { year, period, week } = current;
  switch (key) {
    case 'wtd':
      return timeframeFrom(year, [{ period, weekInPeriod: week }]);
    case 'ptd':
      return timeframeFrom(year, weeksOfPeriod(period, week));
    case 'qtd': {
      const quarterStart = [...WES_QUARTER_START_PERIODS].reverse().find((p) => p <= period) ?? 1;
      const entries: PeriodWeek[] = [];
      for (let p = quarterStart; p <= period; p += 1) entries.push(...weeksOfPeriod(p, p === period ? week : undefined));
      return timeframeFrom(year, entries);
    }
    case 'ytd': {
      const entries: PeriodWeek[] = [];
      for (let p = 1; p <= period; p += 1) entries.push(...weeksOfPeriod(p, p === period ? week : undefined));
      return timeframeFrom(year, entries);
    }
    case 'lcw': {
      let y = year;
      let p = period;
      let w = week - 1;
      if (w < 1) {
        p -= 1;
        w = WES_WEEKS_PER_PERIOD;
        if (p < 1) {
          y -= 1;
          p = WES_PERIODS_PER_YEAR;
        }
      }
      return timeframeFrom(y, [{ period: p, weekInPeriod: w }]);
    }
    case 'lcp': {
      let y = year;
      let p = period - 1;
      if (p < 1) {
        y -= 1;
        p = WES_PERIODS_PER_YEAR;
      }
      return timeframeFrom(y, weeksOfPeriod(p));
    }
    default:
      return { years: [], periods: [], weeks: [] };
  }
};

// ─── Chart grouping ──────────────────────────────────────────────────────────────

export type WesHierarchyLevel = 'region' | 'market' | 'location';
export type WesTimeGrain = 'year' | 'period' | 'week';

export interface WesChartOverrides {
  hierarchy?: WesHierarchyLevel;
  timeframe?: WesTimeGrain;
}

// The lowest filter with a selection sets the level: one value drills one level down, two or more hold, a Location or Week selection shows only the selected values.
export const inferChartLevels = (sel: WesFilterSelection): { hierarchy: WesHierarchyLevel; timeframe: WesTimeGrain } => {
  let hierarchy: WesHierarchyLevel = 'region';
  if (sel.locations.length > 0) hierarchy = 'location';
  else if (sel.markets.length > 0) hierarchy = sel.markets.length === 1 ? 'location' : 'market';
  else if (sel.regions.length > 0) hierarchy = sel.regions.length === 1 ? 'market' : 'region';

  let timeframe: WesTimeGrain = 'year';
  if (sel.weeks.length > 0) timeframe = 'week';
  else if (sel.periods.length > 0) timeframe = sel.periods.length === 1 ? 'week' : 'period';
  else if (sel.years.length > 0) timeframe = sel.years.length === 1 ? 'period' : 'year';

  return { hierarchy, timeframe };
};

const sameChart = (a: WesChartOverrides, b: WesChartOverrides) =>
  a.hierarchy === b.hierarchy && a.timeframe === b.timeframe;

// ─── Hook ────────────────────────────────────────────────────────────────────────

// Component-local session state: nothing is mirrored to Redux, so leaving the report resets everything.
export function useWesPalletStagingFilters() {
  const fiscalYear = useSelector((state: RootState) => state.appData?.periodWeek?.data?.year);
  const [draft, setDraft] = useState<WesFilterSelection>(() => selectionForFiscalYear(fiscalYear));
  const [applied, setApplied] = useState<WesFilterSelection>(() => selectionForFiscalYear(fiscalYear));
  const initializedFiscalYear = useRef(Boolean(fiscalYear));
  const [activeQuickSelect, setActiveQuickSelect] = useState<WesQuickSelectKey | null>(null);
  const [quickValues, setQuickValues] = useState<Timeframe | null>(null);
  // Hand-set Chart Configuration toggles. Desktop writes both copies (immediate); the mobile sheet writes the draft only until Apply.
  const [chartDraft, setChartDraft] = useState<WesChartOverrides>({});
  const [chartApplied, setChartApplied] = useState<WesChartOverrides>({});
  // True once the user picks a Region or Market by hand; only then do they narrow the Location list. Auto-filled parents never do.
  const [manualParents, setManualParents] = useState({ regions: false, markets: false });
  const parentsAreManual = manualParents.regions || manualParents.markets;

  // Period-weeks are fetched for the draft and the applied years, so applied week tokens can be translated to week-in-year.
  const referenceYears = useMemo(() => Array.from(new Set([...draft.years, ...applied.years])).sort(), [draft.years, applied.years]);
  const reference = useWesFilterReference(referenceYears);
  const referenceRef = useRef(reference);
  referenceRef.current = reference;
  const manualParentsRef = useRef(manualParents);
  manualParentsRef.current = manualParents;

  useEffect(() => {
    if (!fiscalYear || initializedFiscalYear.current) return;
    initializedFiscalYear.current = true;
    setDraft((prev) => (prev.years.length > 0 ? prev : { ...prev, years: [String(fiscalYear)] }));
    setApplied((prev) => (prev.years.length > 0 ? prev : { ...prev, years: [String(fiscalYear)] }));
  }, [fiscalYear]);

  const changeFilter = useCallback((key: WesFilterKey, ids: string[]) => {
    const ref = referenceRef.current;
    if (key === 'regions' || key === 'markets') setManualParents((prev) => ({ ...prev, [key]: ids.length > 0 }));
    setDraft((prev) => {
      if (key === 'locations') {
        // Hand-picked Region/Market stay as chosen; otherwise they mirror the selected locations.
        const keepParents = manualParentsRef.current.regions || manualParentsRef.current.markets;
        return prune(
          {
            ...prev,
            locations: ids,
            regions: keepParents ? prev.regions : distinctFromLocations(ref, ids, 'region'),
            markets: keepParents ? prev.markets : distinctFromLocations(ref, ids, 'market'),
          },
          ref,
        );
      }
      if (key === 'years') return prune({ ...prev, years: ids, periods: [], weeks: [] }, ref);
      return prune({ ...prev, [key]: ids }, ref);
    });
  }, []);

  const setChartToggle = useCallback((change: WesChartOverrides, immediate: boolean) => {
    setChartDraft((prev) => ({ ...prev, ...change }));
    if (immediate) setChartApplied((prev) => ({ ...prev, ...change }));
  }, []);

  // Apply commits toggles changed in the mobile sheet; otherwise it returns both toggles to the inferred levels.
  const apply = useCallback(() => {
    if (quickValues && !sameTimeframe(draft, quickValues)) {
      setActiveQuickSelect(null);
      setQuickValues(null);
    }
    setApplied(draft);
    const next = sameChart(chartDraft, chartApplied) ? {} : chartDraft;
    setChartDraft(next);
    setChartApplied(next);
  }, [draft, quickValues, chartDraft, chartApplied]);

  const reset = useCallback(() => {
    const year = referenceRef.current.currentPeriodWeek?.year ?? fiscalYear;
    const selection = selectionForFiscalYear(year);
    setDraft(selection);
    setApplied(selection);
    setActiveQuickSelect(null);
    setQuickValues(null);
    setChartDraft({});
    setChartApplied({});
    setManualParents({ regions: false, markets: false });
  }, [fiscalYear]);

  // Quick selects apply immediately, dropping draft-only edits while retaining previously applied filters.
  const pressQuickSelect = useCallback(
    (key: WesQuickSelectKey) => {
      const current = referenceRef.current.currentPeriodWeek;
      if (!current) return;
      const timeframe: Timeframe =
        activeQuickSelect === key ? { years: [String(current.year)], periods: [], weeks: [] } : quickSelectTimeframe(key, current);
      const next = prune({ ...applied, ...timeframe }, referenceRef.current);
      setDraft(next);
      setApplied(next);
      setChartDraft({});
      setChartApplied({});
      if (activeQuickSelect === key) {
        setActiveQuickSelect(null);
        setQuickValues(null);
      } else {
        setActiveQuickSelect(key);
        setQuickValues(timeframe);
      }
    },
    [activeQuickSelect, applied],
  );

  const discardDraft = useCallback(() => {
    setDraft(applied);
    setChartDraft(chartApplied);
  }, [applied, chartApplied]);

  const currentYear = reference.currentPeriodWeek?.year ?? fiscalYear ?? new Date().getFullYear();

  const options = useMemo(
    () => ({
      regions: regionOptions(reference.locations),
      markets: marketOptions(reference.locations, draft.regions),
      locations: parentsAreManual ? locationOptions(reference.locations, draft.regions, draft.markets) : locationOptions(reference.locations, [], []),
      years: yearOptions(currentYear),
      periods: periodOptions(),
      weeks: weekOptions(reference.periodWeeks, draft.periods),
    }),
    [reference.locations, reference.periodWeeks, draft.regions, draft.markets, draft.periods, currentYear, parentsAreManual],
  );

  // Future periods and weeks are shown but not selectable once a single year equal to the current year is chosen, as in Crewing.
  const selectedYearNumbers = useMemo(() => draft.years.map(Number).filter((n) => !Number.isNaN(n)), [draft.years]);
  const { disabledPeriodIds } = useDisabledPeriodWeekIds({
    currentPeriodWeek: reference.currentPeriodWeek,
    selectedYearNumbers,
    periodOptions: options.periods,
    weekOptions: options.weeks,
  });
  const disabledWeekIds = useMemo(() => {
    const current = reference.currentPeriodWeek;
    const disabled = new Set<string>();
    if (!current || selectedYearNumbers.length !== 1 || selectedYearNumbers[0] !== current.year) return disabled;
    const currentWeekInYear = (current.period - 1) * WES_WEEKS_PER_PERIOD + current.week;
    options.weeks.forEach((option) => {
      if (option.weekInYear > currentWeekInYear) disabled.add(String(option.id));
    });
    return disabled;
  }, [reference.currentPeriodWeek, selectedYearNumbers, options.weeks]);

  const inferred = useMemo(() => inferChartLevels(applied), [applied]);
  const chart = useMemo(
    () => ({
      // Levels the charts use (applied) and the levels the toggles show (includes unapplied mobile edits).
      hierarchy: chartApplied.hierarchy ?? inferred.hierarchy,
      timeframe: chartApplied.timeframe ?? inferred.timeframe,
      shownHierarchy: chartDraft.hierarchy ?? inferred.hierarchy,
      shownTimeframe: chartDraft.timeframe ?? inferred.timeframe,
    }),
    [chartApplied, chartDraft, inferred],
  );

  return {
    draft,
    applied,
    options,
    disabled: { periods: disabledPeriodIds, weeks: disabledWeekIds },
    reference,
    chart,
    setChartToggle,
    activeQuickSelect,
    isDirty: !sameWesSelection(draft, applied) || !sameChart(chartDraft, chartApplied),
    changeFilter,
    apply,
    reset,
    pressQuickSelect,
    discardDraft,
  };
}

export type WesFilters = ReturnType<typeof useWesPalletStagingFilters>;
export type WesFilterOption = KPIFilterDropdownItem;
